import { isAfter, isBefore, isEqual, isSameDay, isValid } from "date-fns";

/**
 * Validates if a date passes the bounds restrictions. Meant to be used with react-hook-form.
 * @param value a valid date object
 * @param param1.before provide the date that the value must be before
 * @param param1.after provide the date that the value must be after
 * @param param1.fallbackBeforeDate provide the fallback date that the value must be before
 * @param param1.fallbackAfterDate provide the fallback date that the value must be after
 * @param param1.fallbackDateLabel provide the label for the fallback dates
 * @returns boolean true if validation passed, error message otherwise
 */
export function validateDateBounds(
  value: Date | undefined,
  {
    before,
    after,
    fallbackBeforeDate,
    fallbackAfterDate,
    fallbackDateLabel,
  }: {
    before?: Date | string;
    after?: Date | string;
    fallbackBeforeDate?: Date;
    fallbackAfterDate?: Date;
    fallbackDateLabel?: string;
  }
) {
  if (value === undefined) {
    return `Answer must be a valid date.`;
  }
  if (
    fallbackBeforeDate &&
    !isBefore(value, fallbackBeforeDate) &&
    (fallbackDateLabel === "date"
      ? !isSameDay(value, fallbackBeforeDate)
      : !isEqual(value, fallbackBeforeDate))
  ) {
    return `Answer must be before or equal to the current ${fallbackDateLabel}.`;
  }
  if (
    fallbackAfterDate &&
    !isAfter(value, fallbackAfterDate) &&
    (fallbackDateLabel === "date"
      ? !isSameDay(value, fallbackAfterDate)
      : !isEqual(value, fallbackAfterDate))
  ) {
    return `Answer must be after or equal to the current ${fallbackDateLabel}.`;
  }

  if (
    !fallbackBeforeDate &&
    before &&
    !isBefore(value, new Date(before)) &&
    !isEqual(value, new Date(before))
  ) {
    return `Answer must be before or equal to ${before.toString()}.`;
  }

  if (
    !fallbackAfterDate &&
    after &&
    !isAfter(value, new Date(after)) &&
    !isEqual(value, new Date(after))
  ) {
    return `Answer must be after or equal to ${after.toString()}.`;
  }

  return true;
}

/**
 * Check if a date string is valid by parsing it using the provided parser and checking if it's a valid date
 * @param value a date string
 * @param parser a predicate run with the given date string value that should return a Date
 * @returns true if the parsed date is valid, false otherwise
 */
export function validateDate(
  value: string | undefined,
  parser: (v: string) => Date
) {
  if (value === undefined) return false;

  return isValid(parser(value));
}
